import axios from "axios";
import client from "./client-config";
import { HttpResponse } from "../types";

enum HttpMethod {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    DELETE = 'DELETE'
}

export default class HttpClientRequest {

    private static abortControllers: Map<string, AbortController> = new Map();

    static async doPostRequest(endpoint: string, field: any, headers?: any, requestId?: string) {
        return HttpClientRequest.doMakeRequest(endpoint, HttpMethod.POST, field, headers, requestId);
    }
    
    static async doGetRequest(endpoint: string, headers?: any, requestId?: string) {
        return HttpClientRequest.doMakeRequest(endpoint, HttpMethod.GET, null, headers, requestId);
    }

    static async doPutRequest(endpoint: string, field: any, headers?: any, requestId?: string) {
        return HttpClientRequest.doMakeRequest(endpoint, HttpMethod.PUT, field, headers, requestId);
    }

    static async doDeleteRequest(endpoint: string, headers?: any, requestId?: string) {
        return HttpClientRequest.doMakeRequest(endpoint, HttpMethod.DELETE, null, headers, requestId);
    }
    
    static cancelRequest(requestId: string) {
        const controller = this.abortControllers.get(requestId);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(requestId);
        }
    }

    private static async doMakeRequest(endpoint: string, type: HttpMethod, field?: any, headers?: any, requestId?: string): Promise<HttpResponse> {
        let request, err;
        try {

            // If requestId is provided, store the controller for potential cancellation
            if (requestId) {
                const controller = new AbortController();
                this.abortControllers.set(requestId, controller);
                headers = {
                    ...headers,
                    signal: controller.signal
                };
            }

            switch(type) {
                case HttpMethod.GET:
                    request = (await client.get(endpoint, {...headers}));
                    break;
                case HttpMethod.POST:
                    request = (await client.post(endpoint, field, {...headers}));
                    break;
                case HttpMethod.PUT:
                    request = (await client.put(endpoint, field, {...headers}));
                    break;
                case HttpMethod.DELETE:
                    request = (await client.delete(endpoint, {...headers}));
                    break;
                default:
                    throw new Error("Invalid Http verb.");
            }

            // Clean up if request was successful
            if (requestId) {
                this.abortControllers.delete(requestId);
            }

        } catch (error) {
            // console.log("client-request =", (error as Error));

            if (requestId) {
                this.abortControllers.delete(requestId);
            }

            // Handle cancellation
            const { response } = (error as any);
            request = response;
            err = response?.data?.message;
            
            // Don't treat canceled requests as errors if you want to handle them differently
            if (axios.isCancel(error)) {
                return {
                    status: 403,
                    data: null,
                    message: 'Request canceled',
                    isCanceled: true
                };
            }
        }

        const { data, status } = request || {};

        return {
            status: status || 200,
            data: data?.data || [],
            message: err || data?.message,
            isCanceled: false
        }
    }

}